import { useState, useCallback } from 'react'
import { useAsyncFn } from '@clara/hooks'
import { FileUrl } from '@clara/pdf-viewer'
import {
  getReimbursementById,
  approveReimbursementById,
  rejectReimbursementById,
  attachmentReimbursementById,
  deleteReimbursementById,
} from '../../api/reimbursement-detail'
import { useMarkAsPaid } from '../../api/mark-as-paid'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

export interface useDetailProps {}

export interface PaymentFormInputs {
  paymentDate: string
  concept?: string
  reference?: string
  paymentMethodNote?: string
  note?: string
}

export interface PaymentFormValues {
  paymentDate: Date
  concept?: string
  reference?: string
  paymentMethodNote?: string
  note?: string
}

export const useDetail = () => {
  const [commentReject, setCommentReject] = useState<string>('')
  const [attachmentFiles, setAttachmentFiles] = useState<FileUrl[]>([])

  //==============================
  // Handle Payment Form
  //==============================

  const {
    formState: { errors },
    control,
    watch,
  } = useForm<PaymentFormValues>({
    // TODO: fix this when markAsPaid is enabled in dropdown
    defaultValues: { paymentDate: new Date() },
    resolver: yupResolver(
      yup.object({
        paymentDate: yup.date().required(),
        concept: yup.string().max(30).optional(),
        reference: yup
          .string()
          .matches(/^(\d{1,6}|)$/)
          .matches(/^[0-9]+$|^$/)
          .optional(),
        paymentMethodNote: yup.string().max(60).optional(),
        note: yup.string().max(200).optional(),
      }),
    ),
    mode: 'onChange',
  })

  const updateCommentReject = (value: string) => {
    setCommentReject(value)
  }

  //==============================
  // Async calls for Reimbursement Detail
  //==============================

  const [reimbursementDetail, getReinbursementDetail] = useAsyncFn(
    async (id: string) => {
      const response = await getReimbursementById(id)
      return response
    },
  )

  const _status = reimbursementDetail.value?.status

  const [approveReimbursement, approveReimbursementAsync] = useAsyncFn(
    async (id: string) => {
      const response = await approveReimbursementById(id)
      return response
    },
  )

  const [rejectReimbursement, rejectReimbursementAsync] = useAsyncFn(
    async (id: string) => {
      const status =
        _status === 'APPROVED'
          ? 'FINANCE_REJECTED'
          : _status === 'PENDING'
            ? 'REJECTED'
            : 'UNKNOWN'

      if (status === 'UNKNOWN')
        throw new Error(
          `Invalid 'status' transition: cannot transition from '${_status}' to an unrecognized status.`,
        )

      const response = await rejectReimbursementById(id, commentReject, status)
      return response
    },
    [commentReject, _status],
  )

  const [cancelReimbursement, cancelReimbursementAsync] = useAsyncFn(
    async (id: any) => {
      const response = await approveReimbursementById(id)
      return response
    },
  )

  const [deleteReimbursement, deleteReimbursementAsync] = useAsyncFn(
    async (id: any) => {
      const response = await deleteReimbursementById(id)
      return response
    },
  )

  const [markAsPaidReimbursement, markAsPaidReimbursementAsync] =
    useMarkAsPaid()

  // =============================
  // Attachment files
  // =============================

  const [attachmentReimbursement, attachmentReimbursementAsync] = useAsyncFn(
    async (file: string) => {
      const response = await attachmentReimbursementById(file)
      return response
    },
  )

  const handleAttachmentFiles = useCallback((files: FileUrl[]) => {
    setAttachmentFiles([...files])
  }, [])

  return {
    //Reinbursement
    reimbursementDetail,
    getReinbursementDetail,
    //Approve reinbursement
    approveReimbursement,
    approveReimbursementAsync,
    //Reject reinbursement
    rejectReimbursement,
    rejectReimbursementAsync,
    //Cancel reinbursement
    cancelReimbursement,
    cancelReimbursementAsync,
    //Delete reinbursement
    deleteReimbursement,
    deleteReimbursementAsync,
    //Comment
    commentReject,
    updateCommentReject,
    //Attachment files
    attachmentReimbursement,
    attachmentReimbursementAsync,
    handleAttachmentFiles,
    attachmentFiles,
    //Mark as Paid
    markAsPaidReimbursement,
    markAsPaidReimbursementAsync,
    //Handle Payment Form
    control,
    errors,
    watch,
  }
}
export type useDetailReturn = ReturnType<typeof useDetail>
